import _ from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { ReactComponent as Reload } from '../../../../assets/icons/renew.svg'
import { usePreviewArtworks } from '../../../../hooks/usePreviewArtworks'
import { useSelectedVariantContext } from '../../context/SelectedVariantContext'
import { DEFAULT_MOCKUP_COLOR } from '../../context/SelectedVariantContext/constants'
import { colorNameToHex } from '../../context/SelectedVariantContext/helper'
import CampaignDesignLoading from '../../upload/tabs/campaign-design/CampaignDesignLoading'
import { getMockup2DGeneratorInstance } from '../preview-live/mockup-2d/Mockup2DGenerator'
import PreviewBackgroundImage from './PreviewBackgroundImage'
import {actionSetPreviewSrc, actionSetPreviewImagesLoaded, actionSetFirstLoadedPreviewImage} from '../../context/SelectedVariantContext/action'

const PreviewArtworkContainer = () => {
    const {fetchPreviews, fetchPreview3D, percentage, pending} = usePreviewArtworks()
    const {
        state: {selectedAttribute, previewImages, reloadPreview, reloadBgPreview, useClientMockup, product, selectedPlacements, mappedAttributes, pickerVariants},
        dispatch,
    } = useSelectedVariantContext()
    const {mockups} = product
    let mockupInfos = _.get(mockups[0], 'meta.mockup_infos', [])
    const [wPreview, setWPreview] = useState(200)
    const [hPreview, setHPreview] = useState(200)

    const [listPreviewImages, setListPreviewImages] = useState([])
    const [firstLoad, setFirstLoad] = useState(true)
    const [preview3DImages, setPreview3DImages] = useState({})
    const [imageLoadStatus, setImageLoadStatus] = useState({}) // {index: 'loaded' | 'error' | null}
    const [firstLoadedImageUrl, setFirstLoadedImageUrl] = useState(null)

    const generateCurrentVariant = () => {
        return Object.keys(mappedAttributes).map(attr => {
            return mappedAttributes[attr].find(variant => variant.is_selected === true)?.value_text
        })
    }

    const isValidHexColor = (colorCode) => {
        const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/
        return hexColorRegex.test(colorCode)
    }

    const getFilteredPickerVariants = useCallback(() => {
        if (!mappedAttributes || !pickerVariants) return []

        const { attributes: productAttributes } = product
        const typeAttributeSets = new Set(productAttributes.filter(({ customized }) => customized).map(({ type }) => type))

        const seen = new Set();
        return pickerVariants.filter((variant, index, self) => {
            const { attributes } = variant
            const filteredAttributes = attributes.filter(({ type }) => typeAttributeSets.has(type))
            if (seen.has(filteredAttributes)) {
                return false
            }
            seen.add(filteredAttributes);

            const filterAtts = filteredAttributes.filter((att) => {
                return mappedAttributes[att.type].find(val => val.value_code === att.value_code && val.is_selected)
            })
            return filteredAttributes.length === filterAtts.length
        })
    }, [mappedAttributes, pickerVariants, product])

    const loadPreviewImages = useCallback(async () => {
        if (!mappedAttributes) return []

        setOnImgError(false)
        setFirstLoad(false)

        const { attributes: productAttributes } = product
        const typeAttributeSets = new Set(productAttributes.filter(({ customized }) => customized).map(({ type }) => type))
        const newPickerVariants = getFilteredPickerVariants()

        if (!useClientMockup || !newPickerVariants.length) {
            if (newPickerVariants.length > 0) {
                const pickerVariantsImages = newPickerVariants.map(v => {
                    let images = []
                    const {attributes} = v
                    const filteredAttributes = attributes.filter(({ type }) => typeAttributeSets.has(type))
                    const attColor = filteredAttributes.find(att => att.type === 'color')
                    const valueText = filteredAttributes.map(att => att.value_text).join(' - ')
                    previewImages.forEach(image => {
                        const {listSku} = image
                        if (listSku.find(sku => sku === v.sku) !== undefined) {
                            if (attColor !== undefined) {
                                images = [...image.images].map(img => {
                                    const color = attColor?.value_code
                                    const hexColor = isValidHexColor(color) ? color : colorNameToHex(color) || DEFAULT_MOCKUP_COLOR
                                    return img.replace(/\/[0-9a-fA-F]{6}\//, `/${hexColor.substring(1)}/`)
                                })
                            } else {
                                images = [...image.images]
                            }

                        }
                    })
                    return {
                        variant: valueText,
                        images
                    }
                }).filter((variantItem, index, self) => index === self.findLastIndex(obj => obj.variant === variantItem.variant))

                setListPreviewImages(pickerVariantsImages)
                dispatch(actionSetPreviewSrc(pickerVariantsImages.map(variant => variant.images)))
                // Reset image load status when previews change
                setImageLoadStatus({})
                setFirstLoadedImageUrl(null)
                dispatch(actionSetPreviewImagesLoaded(false))
            } else {
                setListPreviewImages(previewImages)
                dispatch(actionSetPreviewSrc(previewImages.map(image => image.images)))
                // Reset image load status when previews change
                setImageLoadStatus({})
                setFirstLoadedImageUrl(null)
                dispatch(actionSetPreviewImagesLoaded(false))
            }
        } else {
            const processVariants = async () => {
                const pickerVariantsImages = []
                
                for (const variant of newPickerVariants) {
                    const { attributes } = variant
                    const filteredAttributes = attributes.filter(({ type }) => typeAttributeSets.has(type))
                    const attColor = filteredAttributes.find(att => att.type === 'color')
                    const valueText = filteredAttributes.map(att => att.value_text).join(' - ')
                    const variantKey = filteredAttributes.map(att => att.value_text).join('-')

                    let images = []

                    if (preview3DImages[variantKey]) {
                        images = preview3DImages[variantKey]
                    } else {
                        const artwork = await fetchPreview3D({composite: true, variantId: variant._id})
                        if (!artwork) return

                        const mockup2DGenerator = getMockup2DGeneratorInstance()
                        // mockup2DGenerator.clearCache()
                        mockup2DGenerator.loadMockupInfos(selectedAttribute?.mockup.meta.mockup_infos)

                        try {
                            const mockups = await mockup2DGenerator.render(artwork, attColor?.color_code || '#ffffff')
                            setPreview3DImages(prev => ({ ...prev, [variantKey]: mockups }))
                            images = mockups
                        } catch (error) {
                            console.error('Error rendering mockup:', error)
                            images = []
                        }
                    }

                    pickerVariantsImages.push({
                        variant: valueText,
                        images
                    })
                }

                const filteredVariantsImages = pickerVariantsImages.filter((variantItem, index, self) =>
                    index === self.findLastIndex(obj => obj.variant === variantItem.variant)
                )

                setListPreviewImages(filteredVariantsImages)
                dispatch(actionSetPreviewSrc(filteredVariantsImages.map(variant => variant.images)))
                // Reset image load status when previews change
                setImageLoadStatus({})
                setFirstLoadedImageUrl(null)
                dispatch(actionSetPreviewImagesLoaded(false))
            }

            await processVariants()
        }

    }, [previewImages, mappedAttributes, getFilteredPickerVariants, useClientMockup, fetchPreview3D, product, selectedAttribute, preview3DImages, dispatch])

    // const getInfoPreviewArtworkSlider = () => {
    //     let w = 0
    //     let h = 0
    //     if (document.querySelector('.PreviewArtworkContainer')) {
    //         w = window.innerWidth
    //         if (document.querySelector('#left-sidebar')) {
    //             w = window.innerWidth - document.querySelector('#left-sidebar').offsetWidth
    //         }
    //         if (document.querySelector('#right-sidebar')) {
    //             w = window.innerWidth - document.querySelector('#right-sidebar').offsetWidth
    //         }
    //         h = document.querySelector('.PreviewArtworkContainer').offsetHeight
    //     }
    //     setWPreview(w)
    //     setHPreview(h)
    // }

    useEffect(() => {
        loadPreviewImages()
    }, [loadPreviewImages])

    useEffect(() => {
        if (!useClientMockup) {
            fetchPreviews()
        }
    }, [useClientMockup])

    const [onImgError, setOnImgError] = useState(false)

    const loading = reloadPreview || (pending && !onImgError)

    // Get images from the first variant (for tracking load status)
    // listPreviewImages is an array of {variant, images} objects
    const previewSrc = listPreviewImages[0]?.images ?? []
    
    // Calculate visible image indices
    const visibleImageIndices = previewSrc
        .map((src, index) => selectedPlacements.includes(mockupInfos[index]?.name) ? index : null)
        .filter(index => index !== null)
    
    // When loading is done and images are displayed but not tracked, mark them as loaded
    // This handles cases where images render immediately without loading state
    useEffect(() => {
        if (loading || visibleImageIndices.length === 0) return
        
        const allTracked = visibleImageIndices.every(index => 
            imageLoadStatus[index] === 'loaded' || imageLoadStatus[index] === 'error'
        )
        if (allTracked) return

        // If not loading and images are displayed but not all tracked, mark untracked ones as loaded
        setImageLoadStatus(prev => {
            const updated = { ...prev }
            let hasChanges = false

            visibleImageIndices.forEach(index => {
                if (!updated[index] && previewSrc[index]) {
                    updated[index] = 'loaded'
                    hasChanges = true
                }
            })

            return hasChanges ? updated : prev
        })
    }, [loading, visibleImageIndices, previewSrc, imageLoadStatus])
    
    // Check if all visible images are handled (loaded or errored)
    // Only mark as handled if there are visible images AND all are loaded/errored
    // If no visible images, keep loading state (don't auto-mark as loaded)
    const allVisibleImagesHandled = visibleImageIndices.length > 0 && 
        visibleImageIndices.every(index => imageLoadStatus[index] === 'loaded' || imageLoadStatus[index] === 'error')
    
    // Update context when all images are handled
    useEffect(() => {
        dispatch(actionSetPreviewImagesLoaded(allVisibleImagesHandled))
    }, [allVisibleImagesHandled, dispatch])
    
    // Update first loaded image URL in context
    useEffect(() => {
        dispatch(actionSetFirstLoadedPreviewImage(firstLoadedImageUrl))
    }, [firstLoadedImageUrl, dispatch])

    const handleReload = () => {
        if (useClientMockup) {
            // If using 3D previews, just reload the images
            loadPreviewImages()
        } else {
            // Otherwise, fetch from server
            fetchPreviews()
        }
    }

    return (
        <div className={`PreviewArtworkContainer d-flex`}>
            {percentage !== 0 && loading && (
                <div className="LoadingContainer d-flex align-items-center justify-content-center"
                     style={{height: "100%", width: "100%"}}>
                    <CampaignDesignLoading
                        percentage={percentage}
                        text="Your mockup is generating, please wait..."
                    />
                </div>
            )}
            <div className={loading ? 'd-none' : 'd-block'}>
                {onImgError ? (
                    <div className="PreviewArtworkErrorWrapper text-center">
                        <div className="PreviewArtworkError">
                            <div>
                                <p className="text-danger mb-0">Something went wrong, please try again.</p>
                                <Reload width={20} height={20} onClick={handleReload} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="PreviewArtworkWrap">
                        <h2>Final mockups</h2>
                        {
                            listPreviewImages.length > 0 && (
                                listPreviewImages.map((variant, index) => {
                                    return <div key={index}>
                                        <h5>{variant.variant}</h5>
                                        <div className="PreviewArtworkGrid d-flex">
                                            {
                                                variant.images.length > 0 && variant.images.map((src, index) => {
                                                    return selectedPlacements.includes(mockupInfos[index]?.name) ?
                                                        <div key={index} className='Image'>
                                                            {/*{!firstLoad && (*/}
                                                            {/*    <div className='ImageFirstLoading text-center'>*/}
                                                            {/*        <Spinner style={{color: '#007bff'}} />*/}
                                                            {/*    </div>*/}
                                                            {/*)}*/}
                                                            <PreviewBackgroundImage
                                                                index={index}
                                                                imageProp={src}
                                                                reloadBgPreview={reloadBgPreview}
                                                                wPreview={wPreview}
                                                                hPreview={hPreview}
                                                                onImageLoad={() => {
                                                                    if (selectedPlacements.includes(mockupInfos[index]?.name)) {
                                                                        setImageLoadStatus(prev => ({ ...prev, [index]: 'loaded' }))
                                                                        // Set first loaded image URL if not already set
                                                                        if (!firstLoadedImageUrl) {
                                                                            setFirstLoadedImageUrl(src)
                                                                        }
                                                                    }
                                                                }}
                                                                onImageError={() => {
                                                                    if (selectedPlacements.includes(mockupInfos[index]?.name)) {
                                                                        setImageLoadStatus(prev => ({ ...prev, [index]: 'error' }))
                                                                    }
                                                                }}
                                                            />
                                                            <label>{mockupInfos[index]?.name || ''}</label>
                                                        </div> : null
                                                })
                                            }
                                        </div>
                                    </div>
                                })
                            )
                        }
                    </div>
                )}
            </div>
        </div>
    )
}

export default PreviewArtworkContainer